/** @flow */

import type {
  ColourBall,
  LogEntry,
  LogEntryAction,
  MatchAction,
  MatchConfiguration,
  MatchCurrentState,
  MatchState,
  ObjectBall,
  PlayerStats,
  PositiveMatchAction
} from "./types";
import { END_FRAME, END_VISIT, FOUL, POT_COLOUR, POT_RED, START_MATCH, UNDO } from "./MatchActions";
import { BallValues } from "./BallValues";

const initialMatchState = {
  activePlayer: 0,
  frameScore: {
    p0: 0,
    p1: 0
  },
  matchScore: {
    p0: 0,
    p1: 0
  },
  allowUndo: false,
  freeBall: false,
  ballsOn: ['RED'],
  redsRemaining: 15,
  configuration: {
    initialReds: 0,
    player0: '',
    player1: ''
  }
};

function _log(action: PositiveMatchAction, mutableState: MatchState) {
  if (!mutableState.log) {
    mutableState.log = [];
  }
  if (!mutableState.frames) {
    mutableState.frames = [];
  }
  const lastEntry: null | LogEntry = mutableState.log.length > 0 ? mutableState.log[mutableState.log.length - 1] : null;

  const copiedAction: LogEntryAction = {
    type: action.type,
    colour: (action.colour ? ((action.colour: any): ColourBall) : undefined)
  };

  if (action.type === FOUL) {
    copiedAction.value = action.value;
    copiedAction.freeBall = action.freeBall;
    copiedAction.redsLost = action.redsLost;
  }

  if (action.type === POT_COLOUR) {
    copiedAction.asFreeBall = action.asFreeBall;
  }

  if (lastEntry === null) {
    //Put START_MATCH on its own line
    mutableState.log.push({
      id: action.time,
      player: mutableState.current.activePlayer,
      startTime: action.time,
      endTime: action.time,
      text: mutableState.current.activePlayer,
      isScoringVisit: false,
      actions: [copiedAction]
    });
    mutableState.log.push({
      id: action.time,
      player: mutableState.current.activePlayer,
      startTime: action.time,
      endTime: action.time,
      isScoringVisit: false,
      text: mutableState.current.activePlayer,
      actions: []
    });
    return;
  }

  const lastAction = (lastEntry.actions.length > 0) ? lastEntry.actions[lastEntry.actions.length - 1] : null;
  const wasOnColourAfterRed = (lastAction && (lastAction.type === POT_RED || (lastAction.type === POT_COLOUR && lastAction.asFreeBall === 'RED')));
  const playerStats: PlayerStats = lastEntry.player === 0 ? mutableState.player0Stats : mutableState.player1Stats;

  switch (action.type) {
    case END_VISIT:
    case FOUL:
      //last action ended the visit
      lastEntry.endTime = action.time;
      lastEntry.actions.push(copiedAction);
      mutableState.log.push({
        id: action.time,
        player: mutableState.current.activePlayer,
        startTime: action.time,
        endTime: action.time,
        isScoringVisit: false,
        text: mutableState.current.activePlayer,
        actions: []
      });
      if (wasOnColourAfterRed) {
        playerStats.valuesFollowingRed.push(0);
      }
      if (action.type === FOUL) {
        playerStats.foulsConceded.push(action.value);
      }
      break;
    case END_FRAME:
      lastEntry.endTime = action.time;
      if (wasOnColourAfterRed) {
        playerStats.valuesFollowingRed.push(0);
      }
      mutableState.log.push({
        id: action.time,
        player: action.winningPlayerIndex,
        startTime: action.time,
        endTime: action.time,
        isScoringVisit: false,
        text: mutableState.current.activePlayer,
        actions: [copiedAction]
      });
      mutableState.log.push({
        id: action.time,
        player: mutableState.current.activePlayer,
        startTime: action.time,
        endTime: action.time,
        isScoringVisit: false,
        text: mutableState.current.activePlayer,
        actions: []
      });
      break;
    case POT_RED:
    case POT_COLOUR:
      lastEntry.isScoringVisit = true;
      lastEntry.breakTotalBalls = (lastEntry.breakTotalBalls || 0) + 1;

      if (!lastAction) {
        // Start of break
        playerStats.breaks.push({
          balls: 0,
          total: 0,
          startTime: action.time
        })
      }

      const currentPlayerBreak = playerStats.breaks[playerStats.breaks.length - 1];
      currentPlayerBreak.balls += 1;

      if (action.type === POT_RED) {
        lastEntry.breakTotal = (lastEntry.breakTotal || 0) + 1;
        currentPlayerBreak.total += 1;
        if (lastAction && lastAction.type === POT_RED) { // This is the second (or more) legal red in a row.
          if (lastAction.multiplier) {
            lastAction.multiplier++;
          } else {
            lastAction.multiplier = 2;
          }
        } else {
          lastEntry.actions.push(copiedAction); // First red - starts a new shot
        }
      } else if (action.type === POT_COLOUR) {
        const colour: ColourBall = action.colour;
        lastEntry.breakTotal = (lastEntry.breakTotal || 0) + BallValues[action.asFreeBall || colour].value;
        currentPlayerBreak.total += BallValues[action.asFreeBall || colour].value;
        if (wasOnColourAfterRed) {
          playerStats.valuesFollowingRed.push(BallValues[colour].value);
        }
        lastEntry.actions.push(copiedAction);
      }
      lastEntry.endTime = action.time;
      break;
    default:
      console.warn(`Unrecognised action type ${action.type}`);
  }
}

function _applyMatchAction(action: PositiveMatchAction, state: MatchCurrentState): MatchCurrentState {
  if (action.type === START_MATCH) {
    return newMatchState(action);
  }

  if (action.type === END_FRAME) {
    const newMatchScore = {
      ...state.matchScore
    };
    newMatchScore['p' + action.winningPlayerIndex] += 1;
    const newState = newMatchState(state.configuration);
    newState.matchScore = newMatchScore;
    //Wrapping in ternary needed to keep flow happy
    newState.activePlayer = ((initialMatchState.activePlayer + newMatchScore.p0 + newMatchScore.p1) % 2 ? 1 : 0);
    newState.allowUndo = true;
    return newState;
  }

  state.allowUndo = true;
  state.freeBall = false;


  const _endVisit: () => void = () => {
    state.activePlayer = state.activePlayer === 1 ? 0 : 1;
    state.ballsOn = (state.redsRemaining > 0) ?
      ['RED'] :
      (state.ballsOn.length > 1 ? ['YELLOW'] : state.ballsOn);
  };

  const _nextColourBallsOn: (current: ObjectBall) => ObjectBall[] = (current: ObjectBall) => {
    let balls = ['YELLOW', 'GREEN', 'BROWN', 'BLUE', 'PINK', 'BLACK'];
    let ballIdx = balls.indexOf(current);
    if (ballIdx === balls.length - 1) { //Last colour
      return [];
    }
    return [balls[ballIdx + 1]];
  };

  switch (action.type) {
    case POT_RED:
      state.frameScore['p' + state.activePlayer] += 1;
      state.redsRemaining--;
      state.ballsOn = state.redsRemaining > 0 ? ['RED'] : [];
      state.ballsOn.push(
        'YELLOW',
        'GREEN',
        'BROWN',
        'BLUE',
        'PINK',
        'BLACK'
      );
      break;
    case POT_COLOUR:
      state.frameScore['p' + state.activePlayer] += BallValues[action.asFreeBall || action.colour].value;

      //Potted as free ball red
      if (action.asFreeBall === 'RED') {
        state.ballsOn = [
          'RED', //There must be at least one red on
          'YELLOW',
          'GREEN',
          'BROWN',
          'BLUE',
          'PINK',
          'BLACK'
        ];
        return state;
      }

      if (state.redsRemaining > 0) {
        state.ballsOn = ['RED'];
      } else {
        if (state.ballsOn.length > 1) { //We were on any colour when this was potted. It respots
          state.ballsOn = ['YELLOW'];
        } else { //Clearing the colours.
          state.ballsOn = action.asFreeBall ? [action.asFreeBall] : _nextColourBallsOn(action.colour);
        }
      }
      return state;
    case FOUL:
      state.redsRemaining -= action.redsLost;
      _endVisit();
      state.frameScore['p' + state.activePlayer] += action.value;
      state.freeBall = action.freeBall ? state.ballsOn[0] : false;
      return state;
    case END_VISIT:
      _endVisit();
      return state;
    default: {
      throw new Error('Unknown event type: ' + action.type);
    }
  }

  return state;
}

/**
 * Function to annihilate undo actions against previous actions.
 */
function _squashUndos(actions: MatchAction[]): PositiveMatchAction[] {
  //Let Undo actions annihilate previous actions.
  return actions.reduce((prev, current) => {
    if (current.type === UNDO) {
      prev.pop();
    } else {
      prev.push(current);
    }
    return prev;
  }, []);
}

function _copyActions(actions: MatchAction[]): MatchAction[] {
  return actions.map(action => (action.params ? { ...action, params: { ...action.params } } : { ...action }));
}

export function _recalculateState(actions: MatchAction[]): MatchState {
  const reducedActions = _squashUndos(actions);
  const state = deepCopyMatchState();

  reducedActions.forEach((action, i) => {
    state.current = _applyMatchAction(action, state.current);
    _log(action, state);
  });
  state.actions = _copyActions(actions);
  return state;
}

/**
 * This should be more performant than, but exactly equivalent to JSON.parse(JSON.stringify(state))
 *
 * @param state
 * @returns MatchState
 * @private
 */
export function deepCopyMatchState(state: MatchState = {
  current: initialMatchState,
  actions: [],
  log: [],
  frames: [],
  player0Stats: {
    breaks: [],
    valuesFollowingRed: [],
    foulsConceded: []
  },
  player1Stats: {
    breaks: [],
    valuesFollowingRed: [],
    foulsConceded: []
  }
}): MatchState {
  return {
    ...state,
    current: {
      ...state.current,
      configuration: {
        ...state.current.configuration
      },
      frameScore: {
        ...state.current.frameScore
      },
      matchScore: {
        ...state.current.matchScore
      },
      ballsOn: [...state.current.ballsOn]
    },
    actions: _copyActions(state.actions),
    log: state.log.map(entry => ({
      ...entry,
      actions: entry.actions.map(action => ({ ...action }))
    }))
  };
}


function newMatchState(params: MatchConfiguration): MatchCurrentState {
  const newMatch = deepCopyMatchState().current;
  newMatch.configuration = params;
  newMatch.redsRemaining = params.initialReds;
  return newMatch;
}

/**
 * Mutate state according to action. Because this mutates the passed state, it must be wrapped in a call to deepCopyMatchState
 * when used as a reducer. Mutability allows for better performance when reducing a batch of actions before updating global
 * state, such as when rebuilding the store from persisted actions.
 *
 * @param action
 * @param mutableState
 * @returns MatchState
 * @private
 */
export function matchActionHandler(mutableState: MatchState, action: MatchAction) {
  if (!mutableState) {
    mutableState = deepCopyMatchState();
  }

  switch (action.type) {
    case START_MATCH:
      mutableState = ({
        ...deepCopyMatchState(),
        current: newMatchState(action),
        actions: [action]
      }: MatchState);
      _log(action, mutableState);
      return mutableState;
    case END_FRAME:
    case POT_RED:
    case POT_COLOUR:
    case FOUL:
    case END_VISIT:
      mutableState.actions.push(action);
      mutableState.current = _applyMatchAction(action, mutableState.current);
      _log(action, mutableState);
      return mutableState;
    case UNDO:
      if (!mutableState.current.allowUndo) {
        return mutableState;
      }
      mutableState.actions.push(action);
      mutableState = {
        ...mutableState,
        ..._recalculateState(mutableState.actions),
      };
      return mutableState;
    default:
      return mutableState;
  }
}



// WEBPACK FOOTER //
// ./src/match/matchActionHandler.js